"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TBTCToken = exports.TBTCVault = exports.WalletRegistry = exports.Bridge = exports.Address = void 0;
const ethers_1 = require("ethers");
const Bridge_json_1 = __importDefault(require("@keep-network/tbtc-v2/artifacts/Bridge.json"));
const WalletRegistry_json_1 = __importDefault(require("@keep-network/ecdsa/artifacts/WalletRegistry.json"));
const TBTCVault_json_1 = __importDefault(require("@keep-network/tbtc-v2/artifacts/TBTCVault.json"));
const TBTC_json_1 = __importDefault(require("@keep-network/tbtc-v2/artifacts/TBTC.json"));
const backoff_1 = require("./backoff");
const ethereum_helpers_1 = require("./ethereum-helpers");
const bitcoin_1 = require("./bitcoin");
const hex_1 = require("./hex");
const wallet_1 = require("./wallet");
/**
 * Represents an Ethereum address.
 */
// TODO: Make Address extends Hex
class Address {
    // TODO: Make constructor private
    constructor(address) {
        let validAddress;
        try {
            validAddress = ethers_1.utils.getAddress(address);
        }
        catch (e) {
            throw new Error(`Invalid Ethereum address`);
        }
        this.identifierHex = validAddress.substring(2).toLowerCase();
    }
    static from(address) {
        return new Address(address);
    }
    // TODO: Remove once extends Hex
    equals(otherValue) {
        return this.identifierHex === otherValue.identifierHex;
    }
}
exports.Address = Address;
/**
 * Deployed Ethereum contract
 */
class EthereumContract {
    /**
     * @param config Configuration for contract instance initialization.
     * @param deployment Contract Deployment artifact.
     * @param totalRetryAttempts Number of retries for ethereum requests.
     */
    constructor(config, deployment, totalRetryAttempts = 3) {
        var _a, _b;
        this._instance = new ethers_1.Contract((_a = config.address) !== null && _a !== void 0 ? _a : ethers_1.utils.getAddress(deployment.address), `${JSON.stringify(deployment.abi)}`, config.signerOrProvider);
        this._deployedAtBlockNumber =
            (_b = config.deployedAtBlockNumber) !== null && _b !== void 0 ? _b : deployment.receipt.blockNumber;
        this._totalRetryAttempts = totalRetryAttempts;
    }
    /**
     * Get address of the contract instance.
     * @returns Address of this contract instance.
     */
    getAddress() {
        return Address.from(this._instance.address);
    }
    /**
     * Get events emitted by the Ethereum contract.
     * It starts searching from provided block number. If the {@link GetEvents.Options#fromBlock}
     * option is missing it looks for a contract's defined property
     * {@link _deployedAtBlockNumber}. If the property is missing starts searching
     * from block `0`.
     * @param eventName Name of the event.
     * @param options Options for events fetching.
     * @param filterArgs Arguments for events filtering.
     * @returns Array of found events.
     */
    async getEvents(eventName, options, ...filterArgs) {
        var _a;
        // TODO: Test if we need a workaround for querying events from big range in chunks,
        // see: https://github.com/keep-network/tbtc-monitoring/blob/e169357d7b8c638d4eaf73d52aa8f53ee4aebc1d/src/lib/ethereum-helper.js#L44-L73
        return (0, backoff_1.backoffRetrier)((_a = options === null || options === void 0 ? void 0 : options.retries) !== null && _a !== void 0 ? _a : this._totalRetryAttempts)(async () => {
            var _a;
            return await (0, ethereum_helpers_1.getEvents)(this._instance, this._instance.filters[eventName](...filterArgs), (_a = options === null || options === void 0 ? void 0 : options.fromBlock) !== null && _a !== void 0 ? _a : this._deployedAtBlockNumber, options === null || options === void 0 ? void 0 : options.toBlock, options === null || options === void 0 ? void 0 : options.batchedQueryBlockInterval, options === null || options === void 0 ? void 0 : options.logger);
        });
    }
}
// TODO: Refactor code structure as discussed in https://github.com/keep-network/tbtc-v2/pull/460#discussion_r1063383624.
/**
 * Implementation of the Ethereum Bridge handle.
 * @see {ChainBridge} for reference.
 */
class Bridge extends EthereumContract {
    constructor(config) {
        super(config, Bridge_json_1.default);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getDepositRevealedEvents}
     */
    async getDepositRevealedEvents(options, ...filterArgs) {
        const events = await this.getEvents("DepositRevealed", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                fundingTxHash: bitcoin_1.TransactionHash.from(event.args.fundingTxHash).reverse(),
                fundingOutputIndex: ethers_1.BigNumber.from(event.args.fundingOutputIndex).toNumber(),
                depositor: new Address(event.args.depositor),
                amount: ethers_1.BigNumber.from(event.args.amount),
                blindingFactor: hex_1.Hex.from(event.args.blindingFactor).toString(),
                walletPublicKeyHash: hex_1.Hex.from(event.args.walletPubKeyHash).toString(),
                refundPublicKeyHash: hex_1.Hex.from(event.args.refundPubKeyHash).toString(),
                refundLocktime: hex_1.Hex.from(event.args.refundLocktime).toString(),
                vault: event.args.vault === ethers_1.constants.AddressZero
                    ? undefined
                    : new Address(event.args.vault),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#pendingRedemptions}
     */
    async pendingRedemptions(walletPublicKey, redeemerOutputScript) {
        const redemptionKey = Bridge.buildRedemptionKey((0, bitcoin_1.computeHash160)(walletPublicKey), redeemerOutputScript);
        const request = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.pendingRedemptions(redemptionKey);
        });
        return this.parseRedemptionRequest(request, redeemerOutputScript);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#timedOutRedemptions}
     */
    async timedOutRedemptions(walletPublicKey, redeemerOutputScript) {
        const redemptionKey = Bridge.buildRedemptionKey((0, bitcoin_1.computeHash160)(walletPublicKey), redeemerOutputScript);
        const request = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.timedOutRedemptions(redemptionKey);
        });
        return this.parseRedemptionRequest(request, redeemerOutputScript);
    }
    /**
     * Builds a redemption key required to refer a redemption request.
     * @param walletPublicKeyHash The wallet public key hash that identifies the
     *        pending redemption (along with the redeemer output script). Must be
     *        unprefixed.
     * @param redeemerOutputScript The redeemer output script that identifies the
     *        pending redemption (along with the wallet public key hash). Must be
     *        un-prefixed and not prepended with length.
     * @returns The redemption key.
     */
    static buildRedemptionKey(walletPublicKeyHash, redeemerOutputScript) {
        // Convert the output script to raw bytes buffer.
        const rawRedeemerOutputScript = Buffer.from(redeemerOutputScript, "hex");
        // Prefix the output script bytes buffer with 0x and its own length.
        const prefixedRawRedeemerOutputScript = `0x${Buffer.concat([
            Buffer.from([rawRedeemerOutputScript.length]),
            rawRedeemerOutputScript,
        ]).toString("hex")}`;
        // Build the redemption key by using the 0x-prefixed wallet PKH and
        // prefixed output script.
        return ethers_1.utils.solidityKeccak256(["bytes32", "bytes20"], [
            ethers_1.utils.solidityKeccak256(["bytes"], [prefixedRawRedeemerOutputScript]),
            `0x${walletPublicKeyHash}`,
        ]);
    }
    /**
     * Parses a redemption request using data fetched from the on-chain contract.
     * @param request Data of the request.
     * @param redeemerOutputScript The redeemer output script that identifies the
     *        pending redemption (along with the wallet public key hash). Must be
     *        un-prefixed and not prepended with length.
     * @returns Parsed redemption request.
     */
    parseRedemptionRequest(request, redeemerOutputScript) {
        return {
            redeemer: new Address(request.redeemer),
            redeemerOutputScript: redeemerOutputScript,
            requestedAmount: ethers_1.BigNumber.from(request.requestedAmount),
            treasuryFee: ethers_1.BigNumber.from(request.treasuryFee),
            txMaxFee: ethers_1.BigNumber.from(request.txMaxFee),
            requestedAt: ethers_1.BigNumber.from(request.requestedAt).toNumber(),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#revealDeposit}
     */
    async revealDeposit(depositTx, depositOutputIndex, deposit, vault) {
        const depositTxParam = {
            version: `0x${depositTx.version}`,
            inputVector: `0x${depositTx.inputs}`,
            outputVector: `0x${depositTx.outputs}`,
            locktime: `0x${depositTx.locktime}`,
        };
        const revealParam = {
            fundingOutputIndex: depositOutputIndex,
            blindingFactor: `0x${deposit.blindingFactor}`,
            walletPubKeyHash: `0x${deposit.walletPublicKeyHash}`,
            refundPubKeyHash: `0x${deposit.refundPublicKeyHash}`,
            refundLocktime: `0x${deposit.refundLocktime}`,
            vault: vault ? `0x${vault.identifierHex}` : ethers_1.constants.AddressZero,
        };
        const tx = await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.revealDeposit(depositTxParam, revealParam);
        }, this._totalRetryAttempts, undefined, ["Deposit already revealed"]);
        return tx.hash;
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#submitDepositSweepProof}
     */
    async submitDepositSweepProof(sweepTx, sweepProof, mainUtxo, vault) {
        const sweepTxParam = {
            version: `0x${sweepTx.version}`,
            inputVector: `0x${sweepTx.inputs}`,
            outputVector: `0x${sweepTx.outputs}`,
            locktime: `0x${sweepTx.locktime}`,
        };
        const sweepProofParam = {
            merkleProof: `0x${sweepProof.merkleProof}`,
            txIndexInBlock: sweepProof.txIndexInBlock,
            bitcoinHeaders: `0x${sweepProof.bitcoinHeaders}`,
        };
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        const vaultParam = vault
            ? `0x${vault.identifierHex}`
            : ethers_1.constants.AddressZero;
        await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.submitDepositSweepProof(sweepTxParam, sweepProofParam, mainUtxoParam, vaultParam);
        }, this._totalRetryAttempts);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#txProofDifficultyFactor}
     */
    async txProofDifficultyFactor() {
        const txProofDifficultyFactor = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.txProofDifficultyFactor();
        });
        return txProofDifficultyFactor.toNumber();
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#requestRedemption}
     */
    async requestRedemption(walletPublicKey, mainUtxo, redeemerOutputScript, amount) {
        const walletPublicKeyHash = `0x${(0, bitcoin_1.computeHash160)(walletPublicKey)}`;
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        // Convert the output script to raw bytes buffer.
        const rawRedeemerOutputScript = Buffer.from(redeemerOutputScript, "hex");
        // Prefix the output script bytes buffer with 0x and its own length.
        const prefixedRawRedeemerOutputScript = `0x${Buffer.concat([
            Buffer.from([rawRedeemerOutputScript.length]),
            rawRedeemerOutputScript,
        ]).toString("hex")}`;
        await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.requestRedemption(walletPublicKeyHash, mainUtxoParam, prefixedRawRedeemerOutputScript, amount);
        }, this._totalRetryAttempts);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#submitRedemptionProof}
     */
    async submitRedemptionProof(redemptionTx, redemptionProof, mainUtxo, walletPublicKey) {
        const redemptionTxParam = {
            version: `0x${redemptionTx.version}`,
            inputVector: `0x${redemptionTx.inputs}`,
            outputVector: `0x${redemptionTx.outputs}`,
            locktime: `0x${redemptionTx.locktime}`,
        };
        const redemptionProofParam = {
            merkleProof: `0x${redemptionProof.merkleProof}`,
            txIndexInBlock: redemptionProof.txIndexInBlock,
            bitcoinHeaders: `0x${redemptionProof.bitcoinHeaders}`,
        };
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        const walletPublicKeyHash = `0x${(0, bitcoin_1.computeHash160)(walletPublicKey)}`;
        await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.submitRedemptionProof(redemptionTxParam, redemptionProofParam, mainUtxoParam, walletPublicKeyHash);
        }, this._totalRetryAttempts);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#deposits}
     */
    async deposits(depositTxHash, depositOutputIndex) {
        const depositKey = Bridge.buildDepositKey(depositTxHash, depositOutputIndex);
        const deposit = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.deposits(depositKey);
        });
        return this.parseRevealedDeposit(deposit);
    }
    /**
     * Builds the deposit key required to refer a revealed deposit.
     * @param depositTxHash The revealed deposit transaction's hash.
     * @param depositOutputIndex Index of the deposit transaction output that
     *        funds the revealed deposit.
     * @returns Deposit key.
     */
    static buildDepositKey(depositTxHash, depositOutputIndex) {
        const prefixedReversedDepositTxHash = depositTxHash
            .reverse()
            .toPrefixedString();
        return ethers_1.utils.solidityKeccak256(["bytes32", "uint32"], [prefixedReversedDepositTxHash, depositOutputIndex]);
    }
    /**
     * Parses a revealed deposit using data fetched from the on-chain contract.
     * @param deposit Data of the revealed deposit.
     * @returns Parsed revealed deposit.
     */
    parseRevealedDeposit(deposit) {
        return {
            depositor: new Address(deposit.depositor),
            amount: ethers_1.BigNumber.from(deposit.amount),
            vault: deposit.vault === ethers_1.constants.AddressZero
                ? undefined
                : new Address(deposit.vault),
            revealedAt: ethers_1.BigNumber.from(deposit.revealedAt).toNumber(),
            sweptAt: ethers_1.BigNumber.from(deposit.sweptAt).toNumber(),
            treasuryFee: ethers_1.BigNumber.from(deposit.treasuryFee),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#activeWalletPublicKey}
     */
    async activeWalletPublicKey() {
        const activeWalletPublicKeyHash = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.activeWalletPubKeyHash();
        });
        if (activeWalletPublicKeyHash === "0x0000000000000000000000000000000000000000") {
            // If there is no active wallet currently, return undefined.
            return undefined;
        }
        const { walletPublicKey } = await this.wallets(hex_1.Hex.from(activeWalletPublicKeyHash));
        return walletPublicKey.toString();
    }
    async getWalletCompressedPublicKey(ecdsaWalletID) {
        const walletRegistry = await this.walletRegistry();
        const uncompressedPublicKey = await walletRegistry.getWalletPublicKey(ecdsaWalletID);
        return hex_1.Hex.from((0, bitcoin_1.compressPublicKey)(uncompressedPublicKey));
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getNewWalletRegisteredEvents}
     */
    async getNewWalletRegisteredEvents(options, ...filterArgs) {
        const events = await this.getEvents("NewWalletRegistered", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                ecdsaWalletID: hex_1.Hex.from(event.args.ecdsaWalletID),
                walletPublicKeyHash: hex_1.Hex.from(event.args.walletPubKeyHash),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#walletRegistry}
     */
    async walletRegistry() {
        const { ecdsaWalletRegistry } = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.contractReferences();
        });
        return new WalletRegistry({
            address: ecdsaWalletRegistry,
            signerOrProvider: this._instance.signer || this._instance.provider,
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#wallets}
     */
    async wallets(walletPublicKeyHash) {
        const wallet = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.wallets(walletPublicKeyHash.toPrefixedString());
        });
        return this.parseWalletDetails(wallet);
    }
    /**
     * Parses a wallet data using data fetched from the on-chain contract.
     * @param wallet Data of the wallet.
     * @returns Parsed wallet data.
     */
    async parseWalletDetails(wallet) {
        const ecdsaWalletID = hex_1.Hex.from(wallet.ecdsaWalletID);
        return {
            ecdsaWalletID,
            walletPublicKey: await this.getWalletCompressedPublicKey(ecdsaWalletID),
            mainUtxoHash: hex_1.Hex.from(wallet.mainUtxoHash),
            pendingRedemptionsValue: wallet.pendingRedemptionsValue,
            createdAt: wallet.createdAt,
            movingFundsRequestedAt: wallet.movingFundsRequestedAt,
            closingStartedAt: wallet.closingStartedAt,
            pendingMovedFundsSweepRequestsCount: wallet.pendingMovedFundsSweepRequestsCount,
            state: wallet_1.WalletState.parse(wallet.state),
            movingFundsTargetWalletsCommitmentHash: hex_1.Hex.from(wallet.movingFundsTargetWalletsCommitmentHash),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * Builds the UTXO hash based on the UTXO components. UTXO hash is computed as
     * `keccak256(txHash | txOutputIndex | txOutputValue)`.
     *
     * @see {ChainBridge#buildUtxoHash}
     */
    buildUtxoHash(utxo) {
        return hex_1.Hex.from(ethers_1.utils.solidityKeccak256(["bytes32", "uint32", "uint64"], [
            utxo.transactionHash.reverse().toPrefixedString(),
            utxo.outputIndex,
            utxo.value,
        ]));
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getDepositRevealedEvents}
     */
    async getRedemptionRequestedEvents(options, ...filterArgs) {
        // FIXME: Filtering by indexed walletPubKeyHash field may not work
        //        until https://github.com/ethers-io/ethers.js/pull/4244 is
        //        included in the currently used version of ethers.js.
        //        Ultimately, we should upgrade ethers.js to include that fix.
        //        Short-term, we can workaround the problem as presented in:
        //        https://github.com/threshold-network/token-dashboard/blob/main/src/threshold-ts/tbtc/index.ts#L1041C1-L1093C1
        const events = await this.getEvents("RedemptionRequested", options, ...filterArgs);
        return events.map((event) => {
            const prefixedRedeemerOutputScript = hex_1.Hex.from(event.args.redeemerOutputScript);
            const redeemerOutputScript = prefixedRedeemerOutputScript
                .toString()
                .slice((0, bitcoin_1.readCompactSizeUint)(prefixedRedeemerOutputScript).byteLength * 2);
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                walletPublicKeyHash: hex_1.Hex.from(event.args.walletPubKeyHash).toString(),
                redeemer: new Address(event.args.redeemer),
                redeemerOutputScript: redeemerOutputScript,
                requestedAmount: ethers_1.BigNumber.from(event.args.requestedAmount),
                treasuryFee: ethers_1.BigNumber.from(event.args.treasuryFee),
                txMaxFee: ethers_1.BigNumber.from(event.args.txMaxFee),
            };
        });
    }
}
exports.Bridge = Bridge;
/**
 * Implementation of the Ethereum WalletRegistry handle.
 * @see {ChainWalletRegistry} for reference.
 */
class WalletRegistry extends EthereumContract {
    constructor(config) {
        super(config, WalletRegistry_json_1.default);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainWalletRegistry#getWalletPublicKey}
     */
    async getWalletPublicKey(walletID) {
        const publicKey = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.getWalletPublicKey(walletID.toPrefixedString());
        });
        return hex_1.Hex.from(publicKey.substring(2));
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainWalletRegistry#getDkgResultSubmittedEvents}
     */
    async getDkgResultSubmittedEvents(options, ...filterArgs) {
        const events = await this.getEvents("DkgResultSubmitted", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                resultHash: hex_1.Hex.from(event.args.resultHash),
                seed: hex_1.Hex.from(ethers_1.BigNumber.from(event.args.seed).toHexString()),
                result: {
                    submitterMemberIndex: ethers_1.BigNumber.from(event.args.result.submitterMemberIndex),
                    groupPubKey: hex_1.Hex.from(event.args.result.groupPubKey),
                    misbehavedMembersIndices: event.args.result.misbehavedMembersIndices.map((mmi) => ethers_1.BigNumber.from(mmi).toNumber()),
                    signatures: hex_1.Hex.from(event.args.result.signatures),
                    signingMembersIndices: event.args.result.signingMembersIndices.map(ethers_1.BigNumber.from),
                    members: event.args.result.members.map((m) => ethers_1.BigNumber.from(m).toNumber()),
                    membersHash: hex_1.Hex.from(event.args.result.membersHash),
                },
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainWalletRegistry#getDkgResultApprovedEvents}
     */
    async getDkgResultApprovedEvents(options, ...filterArgs) {
        const events = await this.getEvents("DkgResultApproved", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                resultHash: hex_1.Hex.from(event.args.resultHash),
                approver: Address.from(event.args.approver),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainWalletRegistry#getDkgResultChallengedEvents}
     */
    async getDkgResultChallengedEvents(options, ...filterArgs) {
        const events = await this.getEvents("DkgResultChallenged", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                resultHash: hex_1.Hex.from(event.args.resultHash),
                challenger: Address.from(event.args.challenger),
                reason: event.args.reason,
            };
        });
    }
}
exports.WalletRegistry = WalletRegistry;
/**
 * Implementation of the Ethereum TBTCVault handle.
 */
class TBTCVault extends EthereumContract {
    constructor(config) {
        super(config, TBTCVault_json_1.default);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#optimisticMintingDelay}
     */
    async optimisticMintingDelay() {
        const delaySeconds = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.optimisticMintingDelay();
        });
        return ethers_1.BigNumber.from(delaySeconds).toNumber();
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#getMinters}
     */
    async getMinters() {
        const minters = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.getMinters();
        });
        return minters.map(Address.from);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#isMinter}
     */
    async isMinter(address) {
        return await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.isMinter(`0x${address.identifierHex}`);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#isGuardian}
     */
    async isGuardian(address) {
        return await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.isGuardian(`0x${address.identifierHex}`);
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#requestOptimisticMint}
     */
    async requestOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.requestOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, [
            "Optimistic minting already requested for the deposit",
            "The deposit is already swept",
        ]);
        return hex_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#cancelOptimisticMint}
     */
    async cancelOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.cancelOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, ["Optimistic minting already finalized for the deposit"]);
        return hex_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#finalizeOptimisticMint}
     */
    async finalizeOptimisticMint(depositTxHash, depositOutputIndex) {
        const tx = await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.finalizeOptimisticMint(depositTxHash.reverse().toPrefixedString(), depositOutputIndex);
        }, this._totalRetryAttempts, undefined, [
            "Optimistic minting already finalized for the deposit",
            "The deposit is already swept",
        ]);
        return hex_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCVault#optimisticMintingRequests}
     */
    async optimisticMintingRequests(depositTxHash, depositOutputIndex) {
        const depositKey = Bridge.buildDepositKey(depositTxHash, depositOutputIndex);
        const request = await (0, backoff_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.optimisticMintingRequests(depositKey);
        });
        return this.parseOptimisticMintingRequest(request);
    }
    /**
     * Parses a optimistic minting request using data fetched from the on-chain contract.
     * @param request Data of the optimistic minting request.
     * @returns Parsed optimistic minting request.
     */
    parseOptimisticMintingRequest(request) {
        return {
            requestedAt: ethers_1.BigNumber.from(request.requestedAt).toNumber(),
            finalizedAt: ethers_1.BigNumber.from(request.finalizedAt).toNumber(),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingRequestedEvents}
     */
    async getOptimisticMintingRequestedEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingRequested", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                minter: new Address(event.args.minter),
                depositKey: hex_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
                depositor: new Address(event.args.depositor),
                amount: ethers_1.BigNumber.from(event.args.amount),
                fundingTxHash: bitcoin_1.TransactionHash.from(event.args.fundingTxHash).reverse(),
                fundingOutputIndex: ethers_1.BigNumber.from(event.args.fundingOutputIndex).toNumber(),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingCancelledEvents}
     */
    async getOptimisticMintingCancelledEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingCancelled", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                guardian: new Address(event.args.guardian),
                depositKey: hex_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainBridge#getOptimisticMintingFinalizedEvents}
     */
    async getOptimisticMintingFinalizedEvents(options, ...filterArgs) {
        const events = await this.getEvents("OptimisticMintingFinalized", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: hex_1.Hex.from(event.blockHash),
                transactionHash: hex_1.Hex.from(event.transactionHash),
                minter: new Address(event.args.minter),
                depositKey: hex_1.Hex.from(ethers_1.BigNumber.from(event.args.depositKey).toHexString()),
                depositor: new Address(event.args.depositor),
                optimisticMintingDebt: ethers_1.BigNumber.from(event.args.optimisticMintingDebt),
            };
        });
    }
}
exports.TBTCVault = TBTCVault;
/**
 * Implementation of the Ethereum TBTC v2 token handle.
 */
class TBTCToken extends EthereumContract {
    constructor(config) {
        super(config, TBTC_json_1.default);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCToken#totalSupply}
     */
    async totalSupply(blockNumber) {
        return this._instance.totalSupply({
            blockTag: blockNumber !== null && blockNumber !== void 0 ? blockNumber : "latest",
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {ChainTBTCToken#requestRedemption}
     */
    async requestRedemption(walletPublicKey, mainUtxo, redeemerOutputScript, amount) {
        var _a, _b;
        const redeemer = await ((_b = (_a = this._instance) === null || _a === void 0 ? void 0 : _a.signer) === null || _b === void 0 ? void 0 : _b.getAddress());
        if (!redeemer) {
            throw new Error("Signer not provided");
        }
        const vault = await this._instance.owner();
        const extraData = this.buildRequestRedemptionData(Address.from(redeemer), walletPublicKey, mainUtxo, redeemerOutputScript);
        const tx = await (0, ethereum_helpers_1.sendWithRetry)(async () => {
            return await this._instance.approveAndCall(vault, amount, extraData.toPrefixedString());
        }, this._totalRetryAttempts);
        return hex_1.Hex.from(tx.hash);
    }
    buildRequestRedemptionData(redeemer, walletPublicKey, mainUtxo, redeemerOutputScript) {
        const { walletPublicKeyHash, prefixedRawRedeemerOutputScript, mainUtxo: _mainUtxo, } = this.buildBridgeRequestRedemptionData(walletPublicKey, mainUtxo, redeemerOutputScript);
        return hex_1.Hex.from(ethers_1.utils.defaultAbiCoder.encode(["address", "bytes20", "bytes32", "uint32", "uint64", "bytes"], [
            redeemer.identifierHex,
            walletPublicKeyHash,
            _mainUtxo.txHash,
            _mainUtxo.txOutputIndex,
            _mainUtxo.txOutputValue,
            prefixedRawRedeemerOutputScript,
        ]));
    }
    buildBridgeRequestRedemptionData(walletPublicKey, mainUtxo, redeemerOutputScript) {
        const walletPublicKeyHash = `0x${(0, bitcoin_1.computeHash160)(walletPublicKey)}`;
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        // Convert the output script to raw bytes buffer.
        const rawRedeemerOutputScript = Buffer.from(redeemerOutputScript, "hex");
        // Prefix the output script bytes buffer with 0x and its own length.
        const prefixedRawRedeemerOutputScript = `0x${Buffer.concat([
            Buffer.from([rawRedeemerOutputScript.length]),
            rawRedeemerOutputScript,
        ]).toString("hex")}`;
        return {
            walletPublicKeyHash,
            mainUtxo: mainUtxoParam,
            prefixedRawRedeemerOutputScript,
        };
    }
}
exports.TBTCToken = TBTCToken;
//# sourceMappingURL=ethereum.js.map